/**
 * Corridor projection — the fake-3D maths behind the into-the-cave view.
 *
 * Spec: docs/CAVE_VIEW.md §2. Reference: docs/prototype.png panels 3, 4, 9.
 *
 * Everything in the cave lives in corridor space and is projected to screen space
 * here. No WebGL: one perspective divide plus a curve term, applied to DOM/SVG.
 *
 *     u  −1 … +1   across the corridor   (−1 left wall, 0 centre, +1 right wall)
 *     v  −1 … +1   up the corridor wall  (−1 floor,     0 mid,    +1 ceiling)
 *     d   0 … ∞    metres ahead of the camera
 *
 * Pure functions only — no React, no DOM. That is what makes the corridor
 * testable without a renderer, which matters because a projection bug looks like
 * an art bug and is miserable to chase through a screenshot.
 */

/** Focal constant in metres. Larger = flatter perspective. Tuned to the mock. */
export const FOCAL = 9;

/**
 * Light radius (px) per metre of visible depth.
 *
 * Calibrated so the lamp table lands on round numbers:
 *   Candle    light_px 170  →  12 m visible
 *   Shwe Lamp light_px 680  →  48 m visible
 */
export const PX_PER_VISIBLE_METRE = 170 / 12;

/** Reach window, from CONFIG. A rock outside this cannot be swung at. */
export const REACH_FORWARD = 4.6;
export const REACH_BACK = -1.0;

/**
 * Perspective scale. 1 at the camera, asymptotically 0 with distance.
 * Never returns 0, so a sprite is never given a zero size.
 */
export function scale(d: number): number {
  return FOCAL / (FOCAL + Math.max(0, d));
}

/**
 * The curve. A straight tube reads as a rectangle receding; the mock's walls bow
 * inward, so the cross-section narrows a little faster than perspective alone.
 *
 * `tanh`, not `sin`. CAVE_VIEW.md originally specified `1 − 0.18·sin(d/26)`, which
 * is wrong for an endless corridor: sin turns negative past d ≈ 82 m, so the throat
 * would exceed 1 and the far corridor would bulge *outward*. tanh saturates instead
 * — monotonic, and bounded to [0.82, 1] at every depth.
 *
 * The throat must never pinch shut either, or the vanishing point stops reading as
 * an opening and starts reading as a wall.
 */
export function throat(d: number): number {
  return 1 - 0.18 * Math.tanh(Math.max(0, d) / 26);
}

/** How far the equipped lamp lets you see, in metres. */
export function visibleDepth(lightPx: number): number {
  return Math.max(1, lightPx / PX_PER_VISIBLE_METRE);
}

/**
 * Fog. 1 at the camera, 0 at the edge of the lamp's reach.
 *
 * This is the whole lamp fantasy in one line: upgrading raises `lightPx`, which
 * raises `visibleDepth`, which reveals rocks that were already there.
 */
export function opacity(d: number, lightPx: number): number {
  if (d < 0) return 0;
  return clamp(1 - d / visibleDepth(lightPx), 0, 1);
}

/** A rock is swingable only inside the reach window. */
export function inReach(d: number): boolean {
  return d <= REACH_FORWARD && d >= REACH_BACK;
}

/** Past the camera — despawn rather than render behind the player. */
export function isBehind(d: number): boolean {
  return d < REACH_BACK;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Projected {
  /** Screen px from the left edge. */
  x: number;
  /** Screen px from the top edge. */
  y: number;
  /** Multiply a base size by this. */
  scale: number;
  /** 0–1 fog. */
  opacity: number;
  /** Painter's order — nearer rocks must sit above farther ones. */
  z: number;
}

/**
 * Project a corridor point to screen space.
 *
 * The vanishing point sits slightly above centre (0.46 of height) because the
 * camera rides at a miner's eye level looking slightly down at the floor, which
 * is what makes the floor read as a floor rather than a wall.
 */
export function project(u: number, v: number, d: number, view: Viewport, lightPx: number): Projected {
  const s = scale(d);
  const t = throat(d);
  const spread = s * t;

  const cx = view.width / 2;
  const cy = view.height * 0.46;

  return {
    x: cx + u * (view.width / 2) * spread,
    y: cy - v * (view.height / 2) * spread,
    scale: s,
    opacity: opacity(d, lightPx),
    /* Nearer = higher. Offset keeps it positive for the small negative reach. */
    z: Math.round((100 - d) * 10),
  };
}

/**
 * The throat rings, far to near. Each is the same authored arc at a different
 * scale, which is why the curve only has to be drawn once.
 */
export const RING_DEPTHS = [40, 26, 16, 8, 3] as const;

export interface Ring {
  depth: number;
  /** Fraction of the viewport the ring spans. */
  spread: number;
  opacity: number;
  /** Far rings are darker: --cave-wall → --cave-wall-near. */
  tint: string;
}

const RING_TINTS = [
  'var(--cave-wall)',
  'var(--cave-wall)',
  'var(--cave-wall-mid)',
  'var(--cave-wall-mid)',
  'var(--cave-wall-near)',
] as const;

export function rings(lightPx: number): Ring[] {
  return RING_DEPTHS.map((depth, i) => ({
    depth,
    spread: scale(depth) * throat(depth),
    /* Rings stay faintly visible past the lamp's reach — total darkness with no
       silhouette at all reads as a rendering failure rather than as darkness. */
    opacity: Math.max(0.12, opacity(depth, lightPx)),
    tint: RING_TINTS[i] ?? 'var(--cave-wall)',
  }));
}

/** The four surfaces a rock can belong to. Server-owned; see `RockSpawn.slot`. */
export type RockSlot = 'path' | 'floor' | 'wall' | 'ceiling';

/**
 * Placement for a rock, derived from its uuid — the **fallback**, not the rule.
 *
 * The server owns `slot_u` / `slot_v` and has since CAVE_VIEW phase 3; `placementOf` in
 * `field.ts` reads them and only reaches here when a payload predates that. It stays
 * because the alternative fallback is `?? 0`, which drops every rock onto the vanishing
 * point in a single stack.
 *
 * The ranges mirror `run_engine.spawn_rock` so the two agree about what a wall is. `floor`
 * was missing entirely, which mattered: it is 44% of field rock, and this function is
 * total over the slot type, so its absence meant floor rubble silently fell through to
 * the `path` case and was drawn in the middle of the corridor.
 */
export function slotFromUuid(uuid: string, slot: RockSlot): { u: number; v: number } {
  const h = hash(uuid);
  const side = h & 1 ? 1 : -1;
  const a = ((h >>> 1) % 1000) / 1000;
  const b = ((h >>> 11) % 1000) / 1000;

  switch (slot) {
    case 'floor':
      return { u: side * (0.08 + 0.78 * a), v: -0.86 + 0.16 * b };
    case 'wall':
      return { u: side * (0.7 + a * 0.18), v: -0.3 + b * 0.65 };
    case 'ceiling':
      return { u: side * (0.10 + 0.35 * a), v: 0.55 + b * 0.3 };
    case 'path':
    default:
      return { u: side * (0.02 + 0.13 * a), v: -0.55 + b * 0.2 };
  }
}

/** Deterministic 32-bit hash. Same uuid always yields the same slot. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
